import { FC, useMemo } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconFlag } from "@/assets/icons";
import { background, divider, primary, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { typography } from "@/theme/typography";
import { CurriculumGroupAccordion } from "@/features/dashboard/components/learning-progress/CurriculumGroupAccordion";
import { toCurriculumGroups } from "@/features/dashboard/utils/curriculumGroups";
import { LearningProgressSkeleton } from "@/features/dashboard/components/skeletons/LearningProgressSkeleton";
import type { TGetCurriculumProgressResDto } from "@/api/services/main/curriculum-progress/dtos/get-curriculum-progress.dto";

export type TLearningProgressCardProps = {
  progress?: TGetCurriculumProgressResDto;
  isLoading: boolean;
  recommendedSubjectId?: number;
  isTrial?: boolean;
};

export const LearningProgressCard: FC<TLearningProgressCardProps> = ({ progress, isLoading, recommendedSubjectId, isTrial = false }) => {
  const { t } = useTranslation();

  /* Hide entire card if no subjects are learning and no assessments taken */
  const hasAnyActivity = progress?.subjects?.some((s) => s.status !== "not_started" || s.assessedLevel !== null);

  /* WEEKs grouped by 業種/コース — falls back to a single group while BE returns the flat list */
  const groups = useMemo(() => (progress ? toCurriculumGroups(progress) : []), [progress]);

  if (isLoading) {
    return <LearningProgressSkeleton />;
  }

  if (!progress || !hasAnyActivity) return null;

  return (
    <Stack
      sx={{
        borderRadius: rounded.xs,
        border: `1px solid ${divider.high}`,
        bgcolor: background.white,
        p: spacing.sm,
        gap: spacing.mds,
      }}
    >
      {/* Header */}
      <Stack gap={spacing.xs}>
        <Box display="flex" alignItems="center" gap={spacing.xxs}>
          <IconFlag color={primary[500]} width={24} height={24} />
          <Typography sx={{ ...typography.sub16Bold, color: text.primary }}>{t("dashboard.learningProgress.title")}</Typography>
        </Box>
        <Divider sx={{ borderBottom: `1px solid ${divider.high}` }} />
      </Stack>

      {/* Curriculum groups — each holds its own WEEK list */}
      {groups.map((group, index) => (
        <CurriculumGroupAccordion
          key={group.id}
          group={group}
          recommendedSubjectId={recommendedSubjectId}
          isTrial={isTrial}
          subjectIndexOffset={groups.slice(0, index).reduce((count, prev) => count + prev.subjects.length, 0)}
        />
      ))}
    </Stack>
  );
};
